import React, { useState, useEffect, createContext, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import OrdersPanelStatic from './SidePanelStatic';
import { OrderResponse } from '../services/OrderService';
import { useAuth } from '../contexts/AuthContext';
import { useQuota } from '../contexts/QuotaContext';
import { adminService } from '../services/AdminService';
import { useTranslation } from 'react-i18next';
import { useIsOrgAdmin } from '../hooks/useIsOrgAdmin';
import { X, AlertCircle, AlertTriangle, CheckCircle, Info } from 'lucide-react';

interface SystemMessage {
  id: number;
  title: string;
  message: string;
  message_type: string;
}

const typeStyles: Record<string, { wrapper: string; icon: React.FC<any> }> = {
  info:    { wrapper: 'bg-blue-50 border-blue-300 text-blue-800',   icon: Info },
  warning: { wrapper: 'bg-amber-50 border-amber-300 text-amber-800', icon: AlertTriangle },
  error:   { wrapper: 'bg-red-50 border-red-300 text-red-800',       icon: AlertCircle },
  success: { wrapper: 'bg-green-50 border-green-300 text-green-800', icon: CheckCircle },
};

const SystemMessageBanners: React.FC<{ orgId?: number | null }> = ({ orgId }) => {
  const [messages, setMessages] = useState<SystemMessage[]>([]);
  const [dismissed, setDismissed] = useState<Set<number>>(new Set());

  useEffect(() => {
    adminService.getActiveSystemMessages(orgId ?? undefined)
      .then(res => setMessages(res.data?.messages ?? []))
      .catch(() => {/* silently ignore */});
  }, [orgId]);

  const visible = messages.filter(m => !dismissed.has(m.id));
  if (visible.length === 0) return null;

  return (
    <div className="flex flex-col gap-0">
      {visible.map(msg => {
        const style = typeStyles[msg.message_type] ?? typeStyles.info;
        const Icon = style.icon;
        return (
          <div key={msg.id} className={`flex items-start gap-3 px-4 py-2.5 border-b text-sm ${style.wrapper}`}>
            <Icon className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <span className="font-semibold">{msg.title}</span>
              {msg.message && <span className="ml-2 opacity-90">{msg.message}</span>}
            </div>
            <button
              onClick={() => setDismissed(p => new Set(p).add(msg.id))}
              className="flex-shrink-0 opacity-60 hover:opacity-100 transition-opacity"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        );
      })}
    </div>
  );
};

const QuotaBanner: React.FC = () => {
  const { paused } = useQuota();
  const isOrgAdmin = useIsOrgAdmin();
  const { t } = useTranslation();
  const entries = Object.values(paused);
  if (!entries.length) return null;
  const metricNames = entries
    .map((p) => t(`usage.metrics.${p.metric}`, { defaultValue: p.metric.replace('_', ' ') }))
    .join(', ');
  const resetDate = new Date(entries[0].resets_at).toLocaleDateString(undefined, {
    month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC',
  });
  return (
    <div className="flex items-start gap-3 px-4 py-2.5 border-b text-sm bg-red-50 border-red-300 text-red-800">
      <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <span className="font-semibold">
          {t('usage.pausedBanner', {
            defaultValue: '{{metrics}} paused until {{date}} — monthly plan limit reached.',
            metrics: metricNames, date: resetDate,
          })}
        </span>
        {isOrgAdmin && (
          <a href="/usage" className="ml-2 underline font-medium">
            {t('usage.viewUsage', { defaultValue: 'View usage & upgrade' })}
          </a>
        )}
      </div>
    </div>
  );
};

interface SidebarContextType {
  onNewOrderCreated?: (order: OrderResponse) => void;
  onClearChat?: () => void;
  onOrdersUpdate?: (count: number) => void;
  hasNewMessagesForOrder?: (orderId: number) => boolean;
  triggerCreateOrder?: boolean;
  /** Whether the left orders panel is minimized (w-20) vs expanded (w-[212px]).
   *  Consumed by the chat composer to align its left edge to the sidebar. */
  isOrdersPanelMinimized?: boolean;
}

const SidebarContext = createContext<SidebarContextType>({});

export const useSidebarContext = () => useContext(SidebarContext);

interface AppLayoutProps {
  children: React.ReactNode;
  sidebarCallbacks?: SidebarContextType;
  pageTitle?: string;
  pageSubtitle?: string;
}

const AppLayout: React.FC<AppLayoutProps> = ({ children, sidebarCallbacks = {}, pageTitle, pageSubtitle }) => {
  const { currentUser } = useAuth();

  // Initialize minimized state from localStorage to prevent flash
  const [isOrdersPanelMinimized, setIsOrdersPanelMinimized] = useState(() => {
    const saved = localStorage.getItem('ordersPanelMinimized');
    return saved === 'false' ? false : true;
  });

  const handleNewOrderCreated = (order: OrderResponse) => {
    if (sidebarCallbacks.onNewOrderCreated) {
      sidebarCallbacks.onNewOrderCreated(order);
    }
  };

  const handleClearChat = () => {
    if (sidebarCallbacks.onClearChat) {
      sidebarCallbacks.onClearChat();
    }
  };

  const handleOrdersUpdate = (count: number) => {
    if (sidebarCallbacks.onOrdersUpdate) {
      sidebarCallbacks.onOrdersUpdate(count);
    }
  };

  return (
    <SidebarContext.Provider value={{ ...sidebarCallbacks, isOrdersPanelMinimized }}>
      <div className="h-screen flex bg-gray-50 overflow-hidden">
        {/* Left - Orders Panel (Full Height) - Hidden on mobile, visible on desktop */}
        {currentUser && (
          <div className={`hidden lg:flex flex-col flex-shrink-0 h-screen ${isOrdersPanelMinimized ? 'w-20' : 'w-[212px]'}`}>
            <OrdersPanelStatic
              newOrderCreated={handleNewOrderCreated}
              clearChat={handleClearChat}
              onOrdersUpdate={handleOrdersUpdate}
              hasNewMessagesForOrder={sidebarCallbacks.hasNewMessagesForOrder}
              isMinimized={isOrdersPanelMinimized}
              onToggleMinimize={() => setIsOrdersPanelMinimized(!isOrdersPanelMinimized)}
              triggerCreateOrder={sidebarCallbacks.triggerCreateOrder}
            />
          </div>
        )}

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col overflow-hidden min-w-0">
          {/* Page Header - visible on both mobile and desktop */}
          {pageTitle && (
            <div className="hidden lg:flex items-center flex-shrink-0 h-14 px-6 bg-white border-b border-gray-200">
              <div>
                <h1 className="text-lg font-semibold text-gray-900 leading-tight">{pageTitle}</h1>
                {pageSubtitle && (
                  <p className="text-xs text-gray-400 italic">{pageSubtitle}</p>
                )}
              </div>
            </div>
          )}
          <div className="flex-1 overflow-hidden flex flex-col min-h-0">
            <SystemMessageBanners orgId={currentUser?.organization_id} />
            <QuotaBanner />
            {children}
          </div>
        </div>
      </div>
    </SidebarContext.Provider>
  );
};

export default AppLayout;
